<template>
    <div>
        <div class="d-flex flex-row align-content-center align-items-center justify-content-end py-1 px-1 table-display-opt">
            <b-button-group>
                <b-button v-if="$can('create', 'StaffUsers')" @click="addNewUser" variant="primary">
                    Create User
                </b-button>
            </b-button-group>
        </div>
        <b-table
            id="accessControlTable"
            striped
            responsive
            outlined
            show-empty
            :busy="tableState"
            :per-page="perPage"
            :current-page="currentPage"
            :items="data.list"
            :fields="tableFields"
        >
            <!-- Full names -->
            <template #cell(name)="row">
              <div class="d-flex align-items-center">
                <div class="mr-1" v-if="row.item.firstName">
                    <div>
                        <b-avatar
                            :src="row.item.avatar"
                        ></b-avatar>
                    </div>
                </div>
                <div v-if="row.item.firstName">
                  <div class="font-weight-bolder">
                    <span>{{ row.item.firstName }} </span> <span>{{ row.item.lastName }}</span>
                  </div>
                  <div class="font-small-2 text-muted">
                    <b-link :href="'mailto:row.item.email'" target="_blank" rel="noopener noreferrer">
                        {{ row.item.email }}
                    </b-link>
                  </div>
                </div>
              </div>
            </template>

            <!-- User Mobile Phone -->
            <template #cell(phone)="row">
                <div class="d-flex align-items-center" v-if="row.item.phone">
                    <b-link class="custom-info" :href="'tel:row.item.phone'" target="_blank" rel="noopener noreferrer">
                        {{ row.item.phone }}
                    </b-link>
                </div>
            </template>

            <!-- User Roles -->
            <template #cell(role)="row">
                <div class="d-flex align-items-center">
                    <b-badge v-if="row.item.role === 1000" variant="light-danger">Super Admin</b-badge>
                    <b-badge v-if="row.item.role === 500" variant="light-danger">Admin</b-badge>
                    <b-badge v-if="row.item.role === 130" variant="light-info">Manager</b-badge>
                    <b-badge v-if="row.item.role === 125" variant="light-success">Finance/Data Analyst</b-badge>
                    <b-badge v-if="row.item.role === 120" variant="light-primary">Support</b-badge>
                    <b-badge v-if="row.item.role === 115" variant="light-warning">Auditor</b-badge>
                    <b-badge v-if="row.item.role === 110" variant="light-warning">IT/Developer</b-badge>
                    <b-badge v-if="row.item.role === 105" variant="light-primary">Other Staff</b-badge>
                    <b-badge v-if="row.item.role === 100" variant="light">Non Staff</b-badge>
                </div>
            </template>

            <!-- User Address -->
            <template #cell(address)="row">
                <div v-if="!row.item.isBlank">
                    <div class="d-flex align-items-center" v-if="row.item.address">
                        {{ row.item.address }}
                    </div>
                    <div v-else class="d-flex align-items-center">
                        <span class="data-not-available">na</span>
                    </div>
                </div>
            </template>

            <template #cell(authenticator)="row">
                <div v-if="row.item.userId">
                    <b-badge v-if="row.item.totpEnabled" variant="light-success">On</b-badge>
                    <b-badge v-else variant="light-secondary">Off</b-badge>
                </div>
            </template>

            <!-- User Remarks -->
            <template #cell(remark)="row">
                <div v-if="!row.item.isBlank">
                    <div class="d-flex align-items-center" v-if="row.item.remark">
                        {{ row.item.remark }}
                    </div>
                    <div v-else class="d-flex align-items-center">
                        <span class="data-not-available">na</span>
                    </div>
                </div>
            </template>

            <!-- Action -->
            <template #cell(actions)="row">
                <div class="d-flex align-items-center justify-content-center" v-if="row.item.userId">
                    <b-dropdown
                        variant="link"
                        toggle-class="text-decoration-none"
                        no-caret
                        >
                        <template v-slot:button-content>
                            <feather-icon
                                icon="MoreVerticalIcon"
                                size="16"
                                class="text-body align-middle mr-25"
                            />
                        </template>
                        <b-dropdown-item v-if="$can('update', 'StaffUsers')" @click="editUser(row.item)">
                            <feather-icon
                                icon="Edit2Icon"
                                class="mr-50"
                            />
                            <span>Edit</span>
                        </b-dropdown-item>
                        <b-dropdown-item v-if="canResetPassword(row.item)" @click="openResetPassword(row.item)">
                            <feather-icon
                                icon="LockIcon"
                                class="mr-50"
                            />
                            <span>Reset password</span>
                        </b-dropdown-item>
                        <b-dropdown-item v-if="canResetAuthenticator(row.item)" @click="confirmResetAuthenticator(row.item)">
                            <feather-icon
                                icon="ShieldOffIcon"
                                class="mr-50"
                            />
                            <span>Turn off authenticator</span>
                        </b-dropdown-item>
                        <b-dropdown-item v-if="$can('delete', 'StaffUsers')" @click="confirmDelete(row.item)">
                            <feather-icon
                                icon="TrashIcon"
                                class="mr-50"
                            />
                            <span>Delete</span>
                        </b-dropdown-item>
                    </b-dropdown>
                </div>
            </template>
        </b-table>
        <div class="display-pagination-wrapper">
            <b-pagination
                v-model="currentPage"
                :total-rows="rows"
                :per-page="perPage"
                aria-controls="reviewsTable"
                @change="loadNewPage"
            ></b-pagination>
        </div>
        <b-sidebar
            id="addUserSiderbar"
            bg-variant="white"
            right
            backdrop
            shadow
            v-model="addUserSideBar"
            title="Add A New User"
        >
            <sidebar-content-add @refreshUserTable="$emit('refreshUserTable', true)" @close-sidebar="addUserSideBar=false" />
        </b-sidebar>
        <b-sidebar
            id="updateUserSiderbar"
            bg-variant="white"
            right
            backdrop
            shadow
            v-model="updateUserSideBar"
            title="Update Current User"
        >
            <sidebar-content-update :data="rowDataObject" @refreshUserTable="$emit('refreshUserTable', true)" @close-sidebar="updateUserSideBar=false" />
        </b-sidebar>
        <b-modal
            v-model="resetPasswordOpen"
            title="Reset password"
            centered
            no-close-on-backdrop
            @hidden="clearResetPassword"
        >
            <p v-if="resetTarget" class="mb-1">
                Set a new password for
                <strong>{{ resetTargetName }}</strong>
                <span v-if="resetTarget.email">({{ resetTarget.email }})</span>.
                They can sign in with this password.
            </p>
            <b-form @submit.prevent="submitResetPassword">
                <b-form-group label="New password" label-for="staff-reset-password">
                    <b-form-input
                        id="staff-reset-password"
                        v-model="resetPasswordValue"
                        type="password"
                        autocomplete="new-password"
                        required
                    />
                </b-form-group>
                <b-form-group label="Confirm password" label-for="staff-reset-password-confirm">
                    <b-form-input
                        id="staff-reset-password-confirm"
                        v-model="resetPasswordConfirm"
                        type="password"
                        autocomplete="new-password"
                        required
                    />
                </b-form-group>
                <small class="text-muted">
                    At least 8 characters, with an uppercase letter, a lowercase letter, a number, and a special character (!@#$%^&*).
                </small>
            </b-form>
            <template #modal-footer>
                <b-button variant="outline-secondary" :disabled="resetting" @click="resetPasswordOpen = false">
                    Cancel
                </b-button>
                <b-button variant="primary" :disabled="resetting" @click="submitResetPassword">
                    Reset password
                </b-button>
            </template>
        </b-modal>
    </div>
</template>
<script>
import {
    BRow,
    BCol,
    BTable,
    BAvatar,
    BModal,
    BFormGroup,
    BFormInput,
    BFormCheckbox,
    BForm,
    BButton,
    BFormSelect,
    BSidebar
} from 'bootstrap-vue';
import { VueTelInput } from 'vue-tel-input';
import 'vue-tel-input/dist/vue-tel-input.css';
import Ripple from 'vue-ripple-directive';
import { getUserData } from '@/auth/utils'
import { canResetStaffPassword } from '@/libs/acl/staffAbilities'
import SidebarContentAdd from './SidebarAddUser.vue';
import SidebarContentUpdate from './SidebarUpdateUser.vue';
import API from "./api"

export default {
    components: {
        BTable,
        BRow,
        BCol,
        BAvatar,
        BModal,
        BFormGroup,
        BFormInput,
        BFormCheckbox,
        BForm,
        BButton,
        BFormSelect,
        BSidebar,
        VueTelInput,
        SidebarContentAdd,
        SidebarContentUpdate,
        API
    },
    directives: {
        Ripple,
    },
    props: {
        data: {
            type: Object,
            required: true,
        },
        tableState: {
            type: Boolean,
            required: true
        }
    },
    data() {
        return {
            userRole: [
                {
                    text: "Select a role",
                    value: null
                },
                {
                    text: "Admin",
                    value: 101
                },
                {
                    text: "IT/Developer",
                    value: 102
                },
                {
                    text: "Manager",
                    value: 103
                },
                {
                    text: "Finance/Data Analyst",
                    value: 104
                },
                {
                    text: "Guest",
                    value: 105
                }
            ],
            tableFields: [
                // {
                //     key: "userId",
                //     label: "#",
                // },
                {
                    key: "name",
                    label: "User",
                },
                {
                    key: "address",
                    label: "Address",
                },
                {
                    key: "phone",
                    label: "Contact Number",
                },
                {
                    key: "role",
                    label: "Role",
                    sortable: true
                },
                {
                    key: "authenticator",
                    label: "Authenticator",
                },
                {
                    key: "remark",
                    label: "Remark",
                },
                {
                    key: "actions",
                    label: "Actions"
                },
            ],
            perPage: 10,
            currentPage: 1,
            addUserSideBar: false,
            updateUserSideBar: false,
            rowDataObject: {},
            resetPasswordOpen: false,
            resetTarget: null,
            resetPasswordValue: '',
            resetPasswordConfirm: '',
            resetting: false,
            resettingAuthenticator: false,
        }
    },
    computed: {
        rows() {
            return this.data.total;
        },
        resetTargetName() {
            if (!this.resetTarget) return '';
            const name = `${this.resetTarget.firstName || ''} ${this.resetTarget.lastName || ''}`.trim();
            return name || this.resetTarget.email || 'this user';
        },
    },
    mounted() {
        if (this.$route.query.create === '1' && this.$can('create', 'StaffUsers')) {
            this.addUserSideBar = true
        }
    },
    methods: {
        loadNewPage(pageNum) {
            if (this.data.hasMore) {
                this.$emit('changePageNumber', pageNum);
            }
        },
        addNewUser() {
            this.addUserSideBar = true
        },
        editUser(params) {
            this.updateUserSideBar = true;
            this.rowDataObject = params;
        },
        confirmDelete(user) {
            const name = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || 'this user';
            this.$bvModal.msgBoxConfirm(
                `Are you sure you want to delete "${name}"?`,
                {
                    title: `Delete ${name}`,
                    size: 'md',
                    buttonSize: 'sm',
                    okVariant: 'danger',
                    okTitle: 'Delete',
                    cancelTitle: 'Cancel',
                    centered: true,
                },
            ).then(value => {
                if (value) this.deleteUser(user);
            });
        },
        deleteUser(user) {
            const id = user && (user.userId || user._id);
            const name = user
                ? (`${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || 'this user')
                : 'this user';
            if (!id) return;
            API.deleteUser(id).then(res => {
                if (res && res.success) {
                    this.$bvToast.toast(`"${name}" was deleted`, { variant: 'success' });
                    this.$emit('refreshUserTable', true);
                    return;
                }
                this.$bvToast.toast((res && (res.message || res.errorMsg)) || 'Failed to delete user', { variant: 'danger' });
            }).catch(() => {
                this.$bvToast.toast('Failed to delete user', { variant: 'danger' });
            });
        },
        canResetPassword(user) {
            try {
                const current = getUserData() || {};
                return canResetStaffPassword(current.role, user && user.role);
            } catch (error) {
                return false;
            }
        },
        canResetAuthenticator(user) {
            if (!user || !user.userId || !user.totpEnabled || this.resettingAuthenticator) return false;
            return this.$can('manage', 'StaffSecurity');
        },
        confirmResetAuthenticator(user) {
            const name = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || 'this user';
            this.$bvModal.msgBoxConfirm(
                `Turn off the authenticator for ${name}? They will be signed out. The next time they sign in, they scan a new QR code and set it up again.`,
                {
                    title: 'Turn off authenticator',
                    size: 'md',
                    buttonSize: 'sm',
                    okVariant: 'danger',
                    okTitle: 'Turn off',
                    cancelTitle: 'Cancel',
                    centered: true,
                },
            ).then(confirmed => {
                if (confirmed) this.resetAuthenticator(user);
            });
        },
        async resetAuthenticator(user) {
            if (!user || !user.userId || this.resettingAuthenticator) return;
            this.resettingAuthenticator = true;
            try {
                const resp = await API.resetAuthenticator(user.userId);
                if (resp && resp.success) {
                    user.totpEnabled = false;
                    this.$bvToast.toast(`${user.email || 'This user'} will set up a new authenticator the next time they sign in.`, {
                        title: 'Authenticator turned off',
                        variant: 'success',
                    });
                }
            } catch (error) {
                this.$bvToast.toast('Could not turn off authenticator', { title: 'Error', variant: 'danger' });
            } finally {
                this.resettingAuthenticator = false;
            }
        },
        openResetPassword(user) {
            this.resetTarget = user;
            this.resetPasswordValue = '';
            this.resetPasswordConfirm = '';
            this.resetPasswordOpen = true;
        },
        clearResetPassword() {
            this.resetTarget = null;
            this.resetPasswordValue = '';
            this.resetPasswordConfirm = '';
            this.resetting = false;
        },
        checkPassword(value) {
            return /^(?=.*\d)(?=.*[!@#$%^&*])(?=.*[a-z])(?=.*[A-Z]).{8,}$/.test(value || '');
        },
        async submitResetPassword() {
            if (!this.resetTarget || this.resetting) return;
            if (!this.checkPassword(this.resetPasswordValue)) {
                this.$bvToast.toast('Password must be 8+ characters and include an uppercase letter, a lowercase letter, a number, and a special character (!@#$%^&*).', {
                    title: 'Error',
                    variant: 'danger',
                });
                return;
            }
            if (this.resetPasswordValue !== this.resetPasswordConfirm) {
                this.$bvToast.toast('Passwords do not match', { title: 'Error', variant: 'danger' });
                return;
            }
            const target = this.resetTarget;
            const targetName = this.resetTargetName;
            this.resetting = true;
            try {
                const resp = await API.helpResetPassword({
                    userId: target.userId,
                    email: target.email,
                    newPassword: this.resetPasswordValue,
                });
                if (resp && resp.success) {
                    this.$bvToast.toast(`Password updated for ${targetName}.`, {
                        title: 'Success',
                        variant: 'success',
                    });
                    if (this.resetTarget === target) this.resetPasswordOpen = false;
                }
            } catch (error) {
                this.$bvToast.toast('Could not reset password', { title: 'Error', variant: 'danger' });
            } finally {
                if (this.resetTarget === target) this.resetting = false;
            }
        },
    }
}
</script>
